/**
 * @GestionalePD User Question and Answer Page UI
 */
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';
import {
  Container,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  Typography,
  IconButton,
  CardMedia,
  Stack,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
} from '@mui/material';
import { styled } from '@mui/system';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Unstable_Popup as BasePopup } from '@mui/base/Unstable_Popup';
//----------------------------------------------------------------------
import {
  PopupSuccessBody,
  PopupErrorBody,
  POPUP_DURATION_DELAY,
  POPUP_DURATION_SUCCESS,
  POPUP_DURATION_ERROR,
} from '../../utils/popUpStyles';
import { DefaultContainedButton, DefaultOutlinedButton, CustomIconButton } from '../../utils/Buttons';
import { CommonLabel } from '../../utils/Common';
import { HttpErrorHandler } from '../../utils/ErrorHandler';
import { LoadingUI } from '../../utils/LayoutStyles';
import API from '../../api';
/**
 * @returns User Question and Answer Page UI
 */

// Custom popup style
const Popup = styled(BasePopup)`
  z-index: 1400;
`;

export default function SurveyPage() {
  // General
  const [currentProfile, setCurrentProfile] = useState('');

  // Surveys Info
  const [surveys, setSurveys] = useState([]);
  const [isApiCalled, setIsApiCalled] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [isVotedApiCalled, setIsVotedApiCalled] = useState(false);

  /* ------------------------------ Survey Detail ----------------------------- */
  const [openSurveyDetailDialog, setOpenSurveyDetailDialog] = useState(false);
  const [surveyToAnswer, setSurveyToAnswer] = useState({});

  // Open Survey Detail Dialog
  const handleOpenSurveyDetailDialog = (survey) => {
    setIsVotedApiCalled(false);
    const userData = JSON.parse(localStorage.getItem('userData'));
    const userID = userData.id;

    API.get(`/surveys/${survey.id}/checkvote/?user_id=${userID}`)
      .then((response) => {
        setHasVoted(response.data.has_voted);
        setIsVotedApiCalled(true);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });

    setSurveyToAnswer(survey);
    setOpenSurveyDetailDialog(true);
  };

  // Close Survey Detail Dialog
  const handleCloseSurveyDetailDialog = () => {
    setSurveyToAnswer({});
    setOpenSurveyDetailDialog(false);
    setOpen(false);
  };

  /* ------------------------------- Submit Vote ------------------------------ */
  const [openSubmitVote, setOpenSubmitVote] = useState(false);
  const [answerToVote, setAnswerToVote] = useState('');

  // Open Submit Vote Dialog
  const handleOpenSubmitVoteDialog = (answer) => {
    setAnswerToVote(answer);
    setOpenSubmitVote(true);
  };

  // Close Submit Vote Dialog
  const handleCloseSubmitVoteDialog = () => {
    setAnswerToVote('');
    setOpenSubmitVote(false);
    setOpen(false);
  };

  // Submit Vote
  const sendVote = (e) => {
    e.preventDefault();

    const userData = JSON.parse(localStorage.getItem('userData'));
    const userID = userData.id;

    const voteData = {
      survey_id: surveyToAnswer.id,
      user_id: userID,
      answer_id: answerToVote.id,
    };

    API.post(`survey_collect_answers`, voteData)
      .then((response) => {
        showPopup(true, 'Votazione eseguita con successo');
        getUserSurveys();

        setTimeout(() => {
          handleCloseSubmitVoteDialog();
          handleCloseSurveyDetailDialog();
        }, POPUP_DURATION_DELAY);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // Get Initial Surveys List
  useEffect(() => {
    getUserSurveys();
  }, []);

  // Get Surveys List
  const getUserSurveys = () => {
    API.get('/user_surveys?only_card_holder&expand')
      .then((response) => {
        setSurveys(response.data);
        setIsApiCalled(true);
      })
      .catch((error) => {
        setIsApiCalled(true);
      });
  };

  // Get Survey Random Cover
  const getSurveyCover = () => {
    const random = Math.floor(Math.random() * (13 - 1 + 1)) + 1;
    return random;
  };

  // get user data
  useEffect(() => {
    let data;

    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));

    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
    }
  }, []);

  // popup handler
  const [anchor, setAnchor] = useState(null);
  const [open, setOpen] = useState(false);
  const [popUpText, setPopUpText] = useState('');
  const [popupSuccess, setPopupSuccess] = useState('');

  // Show Popup
  const showPopup = (success, text) => {
    setPopupSuccess(success);
    setOpen(true);
    if (text) {
      setPopUpText(text);
    }

    setTimeout(
      () => {
        setOpen(false);
      },
      success ? POPUP_DURATION_SUCCESS : POPUP_DURATION_ERROR
    );
  };

  // popup component
  const displayPopup = () => {
    return (
      <Container ref={setAnchor} sx={{ position: 'absolute', bottom: '-20px' }}>
        <Popup anchor={anchor} open={open}  >
          {popupSuccess ? (
            <PopupSuccessBody sx={{ whiteSpace: 'pre-line' }}>{popUpText}</PopupSuccessBody>
          ) : (
            <PopupErrorBody>{popUpText}</PopupErrorBody>
          )}
        </Popup>
      </Container>
    );
  };

  if (currentProfile && currentProfile[0].prof_type_name !== 'USER') return <Navigate to={{ pathname: '/' }} />;

  return (
    <div>
      <Helmet>
        <title>Questionari</title>
      </Helmet>

      {isApiCalled ? (
        <Container maxWidth="xl">
          {surveys.length > 0 ? (
            <Grid container spacing={3} sx={{ display: 'flex', flexDirection: 'row' }}>
              {surveys.map((survey) => (
                <Grid key={survey.id} item xs={12} md={6} lg={4}>
                  <Card>
                    <CardMedia
                      component="img"
                      alt="cover"
                      height="140"
                      image={`/assets/covers/cover_${getSurveyCover()}.jpg`}
                      sx={{ backgroundSize: 'contain' }}
                    />
                    <CardContent>
                      <Grid container>
                        <Grid item xs={10}>
                          <Typography
                            gutterBottom
                            variant="h5"
                            component="div"
                            sx={{
                              display: 'block',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              maxWidth: '100%',
                              overflow: 'hidden',
                            }}
                          >
                            {survey.survey_name}
                          </Typography>

                          <CommonLabel
                            textColor={survey.survey_status ? '#118d57' : '#b71d18'}
                            bgColor={survey.survey_status ? '#d3efdf' : '#f7ddd8'}
                            text={survey.survey_status ? 'Aperto' : 'Chiuso'}
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <Stack sx={{ height: '100%' }} direction="row" justifyContent="end" alignItems="center">
                            <CustomIconButton
                              sx={{
                                backgroundColor: 'white',
                                border: '1px solid lightgray',
                              }}
                              onClick={() => handleOpenSurveyDetailDialog(survey)}
                            >
                              <NavigateNextIcon />
                            </CustomIconButton>
                          </Stack>

                          <Dialog fullWidth open={openSurveyDetailDialog} onClose={() => {}}>
                            {isVotedApiCalled ? (
                              <DialogContent>
                                <Grid container spacing={3} justifyContent="center">
                                  <Grid item xs={12}>
                                    <Card sx={{ padding: '24px' }}>
                                      <Typography sx={{ fontWeight: 'bold' }}>{surveyToAnswer.survey_name}</Typography>
                                      <Typography>{surveyToAnswer.survey_question}</Typography>
                                    </Card>
                                  </Grid>

                                  <Dialog fullWidth open={openSubmitVote} onClose={() => {}}>
                                    <DialogTitle>Votazione Questionario</DialogTitle>
                                    <DialogContent>
                                      <Typography>Sei sicuro di voler selezionare la seguente risposta?</Typography>
                                      <Typography>{answerToVote.answer_name}</Typography>
                                    </DialogContent>
                                    <DialogActions>
                                      <DefaultContainedButton onClick={sendVote}>Vota</DefaultContainedButton>
                                      <DefaultOutlinedButton onClick={handleCloseSubmitVoteDialog}>
                                        Chiudi
                                      </DefaultOutlinedButton>
                                      {displayPopup()}
                                    </DialogActions>
                                  </Dialog>

                                  {Object.keys(surveyToAnswer).length > 0 &&
                                  surveyToAnswer.survey_answers.length > 0 ? (
                                    surveyToAnswer.survey_answers.map((answer) => (
                                      <Grid key={answer.id} item xs={12}>
                                        <Card>
                                          <IconButton
                                            disabled={hasVoted}
                                            sx={{
                                              width: '100%',
                                              heigh: '100%',
                                              borderRadius: '0',
                                              fontSize: '1em',
                                              padding: '24px',
                                              color: 'black',

                                              '& .MuiTouchRipple-root': {
                                                right: '-200px',
                                                left: '-200px',
                                              },
                                            }}
                                            onClick={() => handleOpenSubmitVoteDialog(answer)}
                                          >
                                            {answer.answer_name}
                                          </IconButton>
                                        </Card>
                                      </Grid>
                                    ))
                                  ) : (
                                    <Grid item xs={12}>
                                      <Card sx={{ padding: '24px' }}>
                                        <Typography>Nessuna risposta disponibile</Typography>
                                      </Card>
                                    </Grid>
                                  )}
                                </Grid>
                              </DialogContent>
                            ) : (
                              <DialogContent>{LoadingUI()}</DialogContent>
                            )}

                            {isVotedApiCalled && (
                              <DialogActions>
                                <Stack
                                  sx={{ width: '100%' }}
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  {hasVoted && (
                                    <Typography sx={{ color: 'red', fontWeight: 'bold', paddingLeft: '12px' }}>
                                      Hai già votato questo questionario
                                    </Typography>
                                  )}

                                  <DefaultOutlinedButton onClick={handleCloseSurveyDetailDialog}>
                                    Chiudi
                                  </DefaultOutlinedButton>
                                </Stack>
                              </DialogActions>
                            )}
                          </Dialog>
                        </Grid>
                      </Grid>
                    </CardContent>
                    <Divider />
                    <Box sx={{ padding: '12px 24px', textAlign: 'center' }}>
                      <Stack width="100%" direction="row" justifyContent="space-between" alignItems="center">
                        <Box sx={{ textAlign: 'center', fontSize: '.9em' }}>
                          <Typography sx={{ fontWeight: 'bold', fontSize: '.9em' }}>Voti:</Typography>
                          {survey.n_users}
                        </Box>
                        <Box sx={{ textAlign: 'center', fontSize: '.9em' }}>
                          <Typography sx={{ fontWeight: 'bold', fontSize: '.9em' }}>Data Inizio:</Typography>
                          {survey.start_date}
                        </Box>
                        <Box sx={{ textAlign: 'center', fontSize: '.9em' }}>
                          <Typography sx={{ fontWeight: 'bold', fontSize: '.9em' }}>Data Fine:</Typography>
                          {survey.end_date}
                        </Box>
                      </Stack>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Stack sx={{ paddingTop: '200px' }} direction="row" justifyContent="center" alignItems="center">
              <Typography>Nessun questionario disponibile</Typography>
            </Stack>
          )}
        </Container>
      ) : (
        <Container maxWidth="xl">{LoadingUI()}</Container>
      )}
    </div>
  );
}
